import * as React from 'react';
import { Button } from '@/components/ui/Button';
import {
  dismissInstallNudge,
  enablePushNotifications,
  isPushSupported,
  wasInstallNudgeDismissed,
} from '@/lib/push';
import { isStandaloneDisplay } from '@/lib/pwa';
import { toast } from 'sonner';

/**
 * After PWA install (or when already standalone), nudge the user to enable notifications.
 * Permission is only requested after an explicit Enable click.
 */
export function PushInstallNudge() {
  const [visible, setVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!isPushSupported() || wasInstallNudgeDismissed()) return;

    const showIfStandalone = () => {
      if (isStandaloneDisplay() && Notification.permission === 'default') {
        setVisible(true);
      }
    };

    showIfStandalone();
    window.addEventListener('appinstalled', showIfStandalone);
    return () => window.removeEventListener('appinstalled', showIfStandalone);
  }, []);

  if (!visible) return null;

  const onEnable = async () => {
    setLoading(true);
    try {
      const ok = await enablePushNotifications();
      if (ok) {
        toast.success('Test notification sent');
        setVisible(false);
      } else {
        toast('Notifications permission was not granted');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not enable notifications');
    } finally {
      setLoading(false);
    }
  };

  const onDismiss = () => {
    dismissInstallNudge();
    setVisible(false);
  };

  return (
    <div className="border-b border-border bg-primary/5 px-4 py-3">
      <div className="mx-auto flex max-w-2xl flex-col gap-2 md:max-w-7xl md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Enable notifications</p>
          <p className="text-xs text-muted-foreground">
            Get a test alert now, then invitations and shared spending on this device.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onDismiss} disabled={loading}>
            Not now
          </Button>
          <Button size="sm" onClick={onEnable} loading={loading}>
            Enable
          </Button>
        </div>
      </div>
    </div>
  );
}
